/**
 * ResultsPage - Página Principal de Resultados
 * @module features/results/pages/ResultsPage
 *
 * Página que exibe os resultados do assessment, incluindo
 * mensagem de conclusão, preview e opções de download.
 *
 * T001-FE: Tela de Resumo com Resultados do Review
 *
 * Estados:
 * - loading: Verificando status do relatório
 * - generating: Relatório sendo gerado (polling)
 * - ready: Relatório disponível para download
 * - error: Falha na geração
 * - unavailable: Endpoint não disponível (graceful degradation)
 */

import { Toaster } from '@/components/ui/sonner';
import {
  getAssessmentType,
  getMessages,
  getSession,
  getSessionId,
  getUserData,
  isSessionComplete,
  useSessionStore,
} from '@/features/chat/stores/sessionStore';
import { cn } from '@/lib/utils';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ErrorView, LoadingView, SuccessView } from '../components';
import { useReportStatus } from '../hooks/useReportStatus';
import type { AssessmentSummary } from '../types';
import { DEFAULT_SPECIALIST_URL } from '../types';

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Página de resultados do assessment
 *
 * Features:
 * - Validação de sessão com redirect
 * - Estados visuais: loading, success, error, unavailable
 * - Download do relatório
 * - Iniciar novo assessment
 * - Link para especialistas
 * - Acessibilidade WCAG AA
 *
 * @example
 * ```tsx
 * <Route path="/results" element={<ResultsPage />} />
 * ```
 */
export function ResultsPage() {
  const navigate = useNavigate();

  // Session store selectors
  const session = useSessionStore(getSession);
  const userData = useSessionStore(getUserData);
  const sessionId = useSessionStore(getSessionId);
  const assessmentType = useSessionStore(getAssessmentType);
  const isCompleted = useSessionStore(isSessionComplete);
  const messages = useSessionStore(getMessages);
  const clearSession = useSessionStore((state) => state.clearSession);

  // FIX-010: Usar mensagens como indicador alternativo de sessão válida
  // Com coleta de dados via IA (Opção B), userData.name pode estar vazio
  const hasValidSession = Boolean(sessionId && isCompleted) || messages.length > 0;

  // FIX-008: Capturar dados da sessão no mount e limpar para permitir novo assessment
  // Usamos refs para manter os dados mesmo após limpar a sessão
  const capturedDataRef = useRef<{
    session: typeof session;
    userData: typeof userData;
    sessionId: string;
    assessmentType: typeof assessmentType;
  } | null>(null);
  const [hasCleared, setHasCleared] = useState(false);

  // FIX-010: Capturar dados no primeiro render válido
  // Removida dependência de userData.name - com coleta via IA, nome pode estar vazio
  if (!capturedDataRef.current && hasValidSession) {
    capturedDataRef.current = {
      session: { ...session },
      userData: { ...userData },
      sessionId,
      assessmentType,
    };
  }

  // Usar dados capturados se disponível, senão usar dados do store
  const displaySession = capturedDataRef.current?.session || session;
  const displayUserData = capturedDataRef.current?.userData || userData;
  const displaySessionId = capturedDataRef.current?.sessionId || sessionId;
  const displayAssessmentType = capturedDataRef.current?.assessmentType || assessmentType;

  // Report status hook - usar sessionId capturado
  const { status, reportUrl, error, isReady, retry, download, isDownloading, scores, insight, pdfReady } =
    useReportStatus(displaySessionId);

  // FIX-008: Scroll to top on mount with smooth animation
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // FIX-008: Limpar sessão quando relatório estiver pronto ou disponível
  // Isso permite que o usuário inicie um novo assessment sem conflitos
  useEffect(() => {
    if (
      capturedDataRef.current &&
      !hasCleared &&
      (status === 'ready' || status === 'unavailable')
    ) {
      clearSession();
      setHasCleared(true);
    }
  }, [status, hasCleared, clearSession]);

  // FIX-010: Validate session - redirect if invalid (apenas se não temos dados capturados)
  useEffect(() => {
    // Se temos dados capturados, não redirecionar (sessão já foi limpa mas temos os dados)
    if (capturedDataRef.current) return;

    // Check if session is valid (removida dependencia de userData.name)
    if (!hasValidSession) {
      navigate('/assessment/selection');
    }
  }, [hasValidSession, navigate]);

  // Build assessment summary (usando dados capturados)
  const summary: AssessmentSummary = useMemo(() => {
    // Calculate duration if we have start and end times
    let durationMinutes: number | undefined;
    if (displaySession.startedAt && displaySession.completedAt) {
      const startTime = new Date(displaySession.startedAt).getTime();
      const endTime = new Date(displaySession.completedAt).getTime();
      durationMinutes = Math.round((endTime - startTime) / 60000);
    }

    // Count questions (user messages)
    const questionsAnswered = displaySession.messages?.filter((m) => m.role === 'user').length;

    return {
      userName: displayUserData?.name || 'Usuário',
      assessmentType: displayAssessmentType || 'tech',
      completedAt: displaySession.completedAt ? new Date(displaySession.completedAt) : new Date(),
      sessionId: displaySessionId,
      questionsAnswered: questionsAnswered > 0 ? questionsAnswered : undefined,
      durationMinutes: durationMinutes && durationMinutes > 0 ? durationMinutes : undefined,
    };
  }, [displaySession, displayUserData, displayAssessmentType, displaySessionId]);

  // Handler for download
  const handleDownload = async () => {
    try {
      await download();
      toast.success('Download iniciado!', {
        description: 'Seu relatório está sendo baixado.',
      });
    } catch {
      toast.error('Falha no download', {
        description: 'Por favor, tente novamente.',
      });
    }
  };

  // Handler for new assessment
  const handleNewAssessment = () => {
    clearSession();
    navigate('/assessment/selection');
  };

  // Get assessment type label for header (usando dados capturados)
  const assessmentTypeLabel =
    displayAssessmentType === 'tech' ? 'Performance Tech' : 'Governança de Produtos';

  // FIX-010: Don't render if session is invalid and não temos dados capturados (redirect in progress)
  if (!capturedDataRef.current && !hasValidSession) {
    return null;
  }

  // Render content based on status
  const renderContent = () => {
    // Loading or Generating states
    if (status === 'loading' || status === 'generating') {
      const message =
        status === 'generating'
          ? 'Gerando seu diagnóstico personalizado...'
          : 'Preparando seu relatório...';
      return <LoadingView message={message} />;
    }

    // Error state
    if (status === 'error') {
      return (
        <ErrorView
          error={error || 'Falha ao gerar relatório. Por favor, tente novamente.'}
          onRetry={retry}
          onNewAssessment={handleNewAssessment}
        />
      );
    }

    // Unavailable state (graceful degradation)
    if (status === 'unavailable') {
      return (
        <SuccessView
          summary={summary}
          reportUrl=""
          onDownload={handleDownload}
          onNewAssessment={handleNewAssessment}
          specialistBookingUrl={DEFAULT_SPECIALIST_URL}
          isDownloading={false}
          scores={scores}
          insight={insight}
          pdfReady={pdfReady}
        />
      );
    }

    // Ready state
    if (isReady && reportUrl) {
      return (
        <SuccessView
          summary={summary}
          reportUrl={reportUrl}
          onDownload={handleDownload}
          onNewAssessment={handleNewAssessment}
          specialistBookingUrl={DEFAULT_SPECIALIST_URL}
          isDownloading={isDownloading}
          scores={scores}
          insight={insight}
          pdfReady={pdfReady}
        />
      );
    }

    // Fallback to loading
    return <LoadingView />;
  };

  return (
    <main data-testid="results-page" className={cn('min-h-screen bg-background', 'flex flex-col')}>
      <Toaster position="top-center" />

      {/* Header */}
      <header
        className={cn(
          'border-b border-border',
          'bg-background/95 backdrop-blur',
          'supports-[backdrop-filter]:bg-background/60',
          'sticky top-0 z-10'
        )}
      >
        <div className="max-w-3xl mx-auto px-4 py-4">
          <h1 className="text-xl font-semibold text-foreground">Resultados do Review</h1>
          <p className="text-sm text-muted-foreground">{assessmentTypeLabel}</p>
        </div>
      </header>

      {/* Content */}
      <div className={cn('flex-1', 'max-w-3xl w-full mx-auto', 'px-4 py-8')}>{renderContent()}</div>
    </main>
  );
}

export default ResultsPage;
